import * as THREE from "three"
import { TextureLoader } from 'three/src/loaders/TextureLoader'; 
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber"
import React, { Suspense, useRef } from "react"

import Stars from "./3d/Stars"
import Effects from "./Shaders/Effects"

import MovableScene from "./Scenes/MovableScene";
import SceneLogo from './Scenes/SceneLogo'
import ScenePlanet from "./Scenes/ScenePlanet"
import ScenePointCloud from "./Scenes/ScenePointCloud"
import ScenePlanetHands from "./Scenes/ScenePlanetHands";
import ScenePhone from "./Scenes/ScenePhone";
import { useAnimationFrame, useWindowSize } from "./util";
import BackgroundShaderPlane from "./Shaders/BackgroundShaderPlane";
import useStore from "./AppStore";
import path from "./path";

function CameraControl({ vec = new THREE.Vector3(), ...props }){
  const camera = useThree((state) => state.camera)
  return useFrame((state, delta) => {
    const initial = useStore.getState().initial
    camera.position.lerp(vec.set(0,0,initial ? 1000 : 10),0.1)
    camera.lookAt(0,-1.5,2)
    camera.updateProjectionMatrix()
  })
}

function BackgroundTexture(props) {
  
  const size = useThree((state) => state.size)
  const scene = useThree((state) => state.scene)

  const [texBg] = useLoader(TextureLoader, [path + '/assets/background-2.png']) 

  return useFrame((state, delta) => {
    const targetAspect = size.width / size.height;
    const imageAspect = 16 / 9;
    const factor = imageAspect / targetAspect;
    // When factor larger than 1, that means texture 'wider' than target。 
    // we should scale texture height to target height and then 'map' the center  of texture to target， and vice versa.
    scene.background = texBg
    scene.background.offset.x = factor > 1 ? (1 - 1 / factor) / 2 : 0;
    scene.background.repeat.x = factor > 1 ? 1 / factor : 1;
    scene.background.offset.y = factor > 1 ? 0 : (1 - factor) / 2;
    scene.background.repeat.y = factor > 1 ? 1 : factor;
  })
}

function SceneLights(props){
  const frontlightRef = useRef(null)
  const backlightRef = useRef(null)
  useFrame(({clock}) => {
    frontlightRef.current.position.x = 13 + Math.sin(clock.elapsedTime*0.7) * 3
    frontlightRef.current.position.y = 17 + Math.cos(clock.elapsedTime*0.9) * 7
  })
  return (
    <group>
      <ambientLight intensity={0.3} />
      <pointLight position={[10,10,10]} intensity={0.5} ref={frontlightRef}/>
      <pointLight position={[-10,-10,-10]} intensity={0.5} ref={backlightRef}/>
    </group>
  )
}

export default function R3FCanvas(props){

  const size = useWindowSize()

  return (
    <Canvas
      perspective
      camera={{ position: [0, 0, 0], near: 0.01, far: 10000 }}
    >

        {/* LIGHTS */}
        <SceneLights />

        {/* SCENES */}
        <Stars count={999} />
        <Suspense fallback={null}>
          <MovableScene
            pin={[0,50,50]}
            startPos={[0,-30,0]}
            endPos={[0,0,10]}
          >
            <SceneLogo />
          </MovableScene>
          <MovableScene
            pin={[150,200,50]}
            startPos={[10,20,-50]}
            endPos={[-10,-20,50]}
          >
            <ScenePlanetHands />
          </MovableScene>
          <MovableScene
            pin={[250,300,50]}
            startPos={[15,20,-50]}
            endPos={[-25,-20,30]}
          >
            <ScenePlanet scale={[2,2,2]} />
          </MovableScene>
          <MovableScene
            pin={[450,470,100]}
            startPos={[0,0,-0]}
            endPos={[0,0,75]}
          >
            <ScenePointCloud />
          </MovableScene>
          <MovableScene
            pin={[570,820,50]}
            startPos={[0,0,-50]}
            endPos={[0,0,10]}
          >
            <ScenePhone flipAt={7300} />
          </MovableScene>
          {/* <SceneBlackHole pin={[900,1000,50]} /> */}
        </Suspense>
        

        {/* POST PROCESSING AND CAMERA */}
        <Effects />
        <CameraControl z={10000}/>
        {size.width <= 768
          ? <BackgroundTexture />
          : <BackgroundShaderPlane position={[0,0,-8000]} />
        }
    </Canvas>
  )
}